import React, { useState } from 'react';
import { ChevronLeft, X, Loader2 } from 'lucide-react';
import { uploadsApi, hasAuthToken } from '../../api';
import { ImageSourceButtons } from '../../components/common/ImageSourceButtons';
import { PaymentViaFields, emptyPaymentVia, validatePaymentVia } from '../../components/common/PaymentViaFields';
import { fileToCompressedDataUrl, formatBytesShort, MAX_PHOTO_BYTES } from '../../lib/imageResize';
import { defaultAdminEntryDate, resolveWorkspaceAsOfDate } from '../../lib/workspaceAsOf';
import { mediaSrc } from '../../lib/mediaUrl';

interface BuyProductProps {
  onBack: () => void;
  onSubmit: (data: Record<string, unknown>) => void;
  setActiveItem?: (item: string) => void;
}

export function BuyProduct({ onBack, onSubmit }: BuyProductProps) {
  const [formData, setFormData] = useState({
    vehicleName: '',
    vehicleNumber: '',
    chasisNumber: '',
    engineNumber: '',
    kms: '',
    repairs: '',
    amountInvested: '',
    cBookNumber: '',
    dealRef: '',
    sellerName: '',
    acquisitionDate: defaultAdminEntryDate() || resolveWorkspaceAsOfDate(),
  });
  const [paymentVia, setPaymentVia] = useState(emptyPaymentVia());
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadNote, setUploadNote] = useState<string | null>(null);

  const investedTotal = Math.max(0, parseFloat(String(formData.amountInvested || '').replace(/,/g, '')) || 0);

  const handlePhotos = async (files: File[]) => {
    if (!files.length) return;
    setUploading(true);
    setUploadNote(null);
    let fellBack = false;
    const next: string[] = [];
    const remaining = Math.max(0, 10 - photos.length);
    for (const file of files.slice(0, remaining)) {
      if (!file.type.startsWith('image/')) continue;
      let dataUrl: string;
      try {
        dataUrl = await fileToCompressedDataUrl(file);
      } catch {
        continue;
      }
      if (!dataUrl) continue;
      if (hasAuthToken()) {
        try {
          const res = await uploadsApi.image(dataUrl, 'consultancy');
          next.push(mediaSrc(res.url));
          continue;
        } catch {
          fellBack = true;
        }
      } else {
        fellBack = true;
      }
      next.push(dataUrl);
    }
    setPhotos((prev) => [...prev, ...next].slice(0, 10));
    setUploading(false);
    if (fellBack) {
      setUploadNote('Saved on this device (cloud upload unavailable — sign in to upload to Cloudinary).');
    } else if (next.length) {
      setUploadNote(`Added ${next.length} photo(s), each under ${formatBytesShort(MAX_PHOTO_BYTES)}.`);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payErr = validatePaymentVia(paymentVia, 'disbursement', investedTotal);
    if (payErr) {
      window.alert(payErr);
      return;
    }
    onSubmit({
      ...formData,
      images: photos,
      paymentVia: paymentVia.paymentVia,
      companyBankId: paymentVia.companyBankId,
      paidBy: paymentVia.paidBy,
      cashAmount: paymentVia.cashAmount,
      bankAmount: paymentVia.bankAmount,
    });
  };

  return (
    <div className="app-page space-y-6">
      <header className="app-page-header">
        <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <button type="button" onClick={onBack} className="app-btn-secondary inline-flex w-fit shrink-0 items-center gap-2 py-2 pl-3 pr-4 text-sm">
            <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden />
            Back
          </button>
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-800">Consultancy · Add Stock</p>
            <h1 className="app-page-title mt-0.5">Add Stock</h1>
            <p className="app-page-sub mt-1">Add a vehicle to your stock.</p>
          </div>
        </div>
      </header>

      <div className="app-card mx-auto max-w-3xl overflow-hidden shadow-sm ring-1 ring-zinc-950/[0.03]">
        <div className="app-card-inner">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
              <div className="md:col-span-2">
                <label className="app-label" htmlFor="buy-deal-ref">
                  Deal reference
                </label>
                <input
                  id="buy-deal-ref"
                  type="text"
                  className="app-field"
                  placeholder="Internal ref / auction lot"
                  value={formData.dealRef}
                  onChange={(e) => setFormData({ ...formData, dealRef: e.target.value })}
                />
              </div>
              <div>
                <label className="app-label" htmlFor="buy-acquisition-date">
                  Acquisition date
                </label>
                <input
                  id="buy-acquisition-date"
                  type="date"
                  className="app-field"
                  value={formData.acquisitionDate}
                  onChange={(e) => setFormData({ ...formData, acquisitionDate: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="app-label" htmlFor="buy-vehicle-name">
                  Vehicle name
                </label>
                <input
                  id="buy-vehicle-name"
                  type="text"
                  className="app-field"
                  placeholder="Make & model"
                  value={formData.vehicleName}
                  onChange={(e) => setFormData({ ...formData, vehicleName: e.target.value })}
                />
              </div>
              <div>
                <label className="app-label" htmlFor="buy-kms">
                  Kilometer driven
                </label>
                <input
                  id="buy-kms"
                  type="number"
                  min={0}
                  step={1}
                  className="app-field"
                  placeholder="Odometer reading (km)"
                  value={formData.kms}
                  onChange={(e) => setFormData({ ...formData, kms: e.target.value })}
                />
              </div>
              <div>
                <label className="app-label" htmlFor="buy-seller">
                  Seller / source
                </label>
                <input
                  id="buy-seller"
                  type="text"
                  className="app-field"
                  placeholder="Dealer, auction, customer"
                  value={formData.sellerName}
                  onChange={(e) => setFormData({ ...formData, sellerName: e.target.value })}
                />
              </div>
              <div>
                <label className="app-label" htmlFor="buy-vehicle-number">
                  Vehicle number
                </label>
                <input
                  id="buy-vehicle-number"
                  type="text"
                  className="app-field uppercase"
                  placeholder="Registration number"
                  autoCapitalize="characters"
                  value={formData.vehicleNumber}
                  onChange={(e) => setFormData({ ...formData, vehicleNumber: e.target.value.toUpperCase() })}
                />
                <p className="mt-1 text-[11px] text-zinc-500">Always saved in CAPITAL letters</p>
              </div>
              <div>
                <label className="app-label" htmlFor="buy-chassis">
                  Chassis number
                </label>
                <input
                  id="buy-chassis"
                  type="text"
                  className="app-field uppercase"
                  placeholder="Chassis number"
                  autoCapitalize="characters"
                  value={formData.chasisNumber}
                  onChange={(e) => setFormData({ ...formData, chasisNumber: e.target.value.toUpperCase() })}
                />
              </div>
              <div>
                <label className="app-label" htmlFor="buy-engine">
                  Engine number
                </label>
                <input
                  id="buy-engine"
                  type="text"
                  className="app-field uppercase"
                  placeholder="Engine number"
                  autoCapitalize="characters"
                  value={formData.engineNumber}
                  onChange={(e) => setFormData({ ...formData, engineNumber: e.target.value.toUpperCase() })}
                />
              </div>
              <div className="md:col-span-2">
                <label className="app-label" htmlFor="buy-repairs">
                  Repairs / recon
                </label>
                <textarea
                  id="buy-repairs"
                  className="app-field min-h-[5rem] resize-y"
                  placeholder="PDI findings, parts, estimated recon cost notes"
                  rows={3}
                  value={formData.repairs}
                  onChange={(e) => setFormData({ ...formData, repairs: e.target.value })}
                />
              </div>
              <div>
                <label className="app-label" htmlFor="buy-amount">
                  Amount invested (₹)
                </label>
                <input
                  id="buy-amount"
                  type="number"
                  min={0}
                  className="app-field"
                  placeholder="Landed cost"
                  value={formData.amountInvested}
                  onChange={(e) => setFormData({ ...formData, amountInvested: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="app-label" htmlFor="buy-cbook">
                  C-book number
                </label>
                <input
                  id="buy-cbook"
                  type="text"
                  className="app-field"
                  placeholder="C-book number"
                  value={formData.cBookNumber}
                  onChange={(e) => setFormData({ ...formData, cBookNumber: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <PaymentViaFields
                  mode="disbursement"
                  value={paymentVia}
                  onChange={setPaymentVia}
                  totalAmount={investedTotal}
                />
              </div>
            </div>

            <div>
              <label className="app-label">Vehicle photos (up to 10)</label>
              <div className="flex flex-wrap items-center gap-3">
                {photos.map((src, i) => (
                  <div key={i} className="relative h-20 w-28 overflow-hidden rounded-lg border border-zinc-200">
                    <img src={mediaSrc(src)} alt={`Vehicle ${i + 1}`} className="h-full w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setPhotos((prev) => prev.filter((_, idx) => idx !== i))}
                      className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80"
                      aria-label="Remove photo"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              {photos.length < 10 ? (
                <div className="mt-3">
                  <ImageSourceButtons
                    onFiles={(f) => void handlePhotos(f)}
                    disabled={uploading}
                    multiple
                    size="sm"
                  />
                  {uploading ? (
                    <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-zinc-500">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Compressing &amp; uploading…
                    </p>
                  ) : null}
                </div>
              ) : null}
              {uploadNote ? <p className="mt-2 text-xs text-zinc-500">{uploadNote}</p> : null}
              <p className="mt-1 text-[11px] text-zinc-400">
                Upload from gallery or take a photo. Images over {formatBytesShort(MAX_PHOTO_BYTES)} are auto-compressed.
              </p>
            </div>

            <div className="flex flex-wrap justify-end gap-2 border-t border-zinc-100 pt-6">
              <button type="button" onClick={onBack} className="app-btn-secondary">
                Cancel
              </button>
              <button type="submit" className="app-btn-primary">
                Save &amp; add to stock
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
